export const TagType = {
  GENERAL: 'GENERAL',
  LOCATION: 'LOCATION',
  DATE_TIME: 'DATE_TIME',
} as const;

export type TagType = (typeof TagType)[keyof typeof TagType];

/** Sighting type for categorising posts */
export const SightingType = {
  MISSED_CONNECTION: 'MISSED_CONNECTION',
  GET_TO_KNOW: 'GET_TO_KNOW',
  LOOK_FAMILIAR: 'LOOK_FAMILIAR',
  GIVE_THANKS: 'GIVE_THANKS',
  NETWORK_ENCOUNTER: 'NETWORK_ENCOUNTER',
  MISCELLANEOUS: 'MISCELLANEOUS',
} as const;

export type SightingType = (typeof SightingType)[keyof typeof SightingType];

/** Pairing constellation: poster gender → other person gender. */
export const PairingType = {
  M_M: 'M_M',
  W_W: 'W_W',
  M_W: 'M_W',
  W_M: 'W_M',
  OTHER: 'OTHER',
} as const;

export type PairingType = (typeof PairingType)[keyof typeof PairingType];

/** Display labels for pairing options; {@link getLocalizedPairingTypeLabel} supplies
 * per-language strings (German uses Mann/Frau instead of M/W). */
export const PAIRING_TYPE_OPTIONS: { value: PairingType; label: string }[] = [
  { value: 'M_M', label: 'M-M' },
  { value: 'W_W', label: 'W-W' },
  { value: 'M_W', label: 'M-W' },
  { value: 'W_M', label: 'W-M' },
  { value: 'OTHER', label: 'not relevant' },
];

const PAIRING_TYPE_TRANSLATIONS: Record<'en' | 'de', Record<PairingType, string>> = {
  en: { M_M: 'M-M', W_W: 'W-W', M_W: 'M-W', W_M: 'W-M', OTHER: 'not relevant' },
  de: {
    M_M: 'Mann-Mann',
    W_W: 'Frau-Frau',
    M_W: 'Mann-Frau',
    W_M: 'Frau-Mann',
    OTHER: 'nicht relevant',
  },
};

export function getLocalizedPairingTypeLabel(
  pairingType: string | undefined,
  language: 'en' | 'de' = 'en'
): string | undefined {
  if (!pairingType) return undefined;
  if (pairingType in PAIRING_TYPE_TRANSLATIONS.en) {
    return PAIRING_TYPE_TRANSLATIONS[language][pairingType as PairingType];
  }
  return pairingType;
}

export const SIGHTING_TYPE_OPTIONS: { value: SightingType; label: string; emoji: string }[] = [
  { value: 'MISSED_CONNECTION', label: 'Missed Connection', emoji: '❤️' },
  { value: 'GET_TO_KNOW', label: 'Get to Know', emoji: '🤝' },
  { value: 'LOOK_FAMILIAR', label: 'Look Familiar', emoji: '🧠' },
  { value: 'GIVE_THANKS', label: 'Give Thanks & Praise', emoji: '🙏' },
  { value: 'NETWORK_ENCOUNTER', label: 'Network & Encounter', emoji: '🌍' },
  { value: 'MISCELLANEOUS', label: 'Miscellaneous', emoji: '👽' },
];

const SIGHTING_TYPE_TRANSLATIONS: Record<'en' | 'de', Record<SightingType, string>> = {
  en: {
    MISSED_CONNECTION: 'Missed Connection',
    GET_TO_KNOW: 'Get to Know',
    LOOK_FAMILIAR: 'Look Familiar',
    GIVE_THANKS: 'Give Thanks & Praise',
    NETWORK_ENCOUNTER: 'Network & Encounter',
    MISCELLANEOUS: 'Miscellaneous',
  },
  de: {
    MISSED_CONNECTION: 'Verpasste Begegnung',
    GET_TO_KNOW: 'Kennenlernen',
    LOOK_FAMILIAR: 'Bekannt vorkommen',
    GIVE_THANKS: 'Danke sagen & Lob',
    NETWORK_ENCOUNTER: 'Netzwerk & Begegnung',
    MISCELLANEOUS: 'Sonstiges',
  },
};

export const POST_LANGUAGE_OPTIONS = [
  { value: 'en', label: 'English' },
  { value: 'de', label: 'German' },
] as const;

/** Get sighting type string from a post (handles both camelCase and snake_case from API). */
export function getSightingType(post: Post | { sightingType?: string; sighting_type?: string } | null | undefined): string | undefined {
  if (!post || typeof post !== 'object') return undefined;
  const p = post as { sightingType?: string; sighting_type?: string };
  const value = p.sightingType ?? p.sighting_type;
  if (value == null || typeof value !== 'string' || String(value).trim() === '') return undefined;
  return String(value).trim();
}

/** Get display info for a sighting type (emoji + label). */
export function getLocalizedSightingTypeLabel(
  sightingType: string | undefined,
  language: 'en' | 'de' = 'en'
): string | undefined {
  if (!sightingType) return undefined;
  if (sightingType in SIGHTING_TYPE_TRANSLATIONS.en) {
    return SIGHTING_TYPE_TRANSLATIONS[language][sightingType as SightingType];
  }
  return sightingType;
}

/** Get display info for a sighting type (emoji + label). */
export function getSightingTypeDisplay(
  sightingType: string | undefined,
  language: 'en' | 'de' = 'en'
): { emoji: string; label: string } | null {
  if (!sightingType) return null;
  const opt = SIGHTING_TYPE_OPTIONS.find(o => o.value === sightingType);
  const label = getLocalizedSightingTypeLabel(sightingType, language) ?? sightingType;
  return opt ? { emoji: opt.emoji, label } : { emoji: '', label };
}

export interface PostTag {
  id: string;
  postId: string;
  tagType: TagType;
  tagValue: string;
  locationLatitude?: number;
  locationLongitude?: number;
  locationName?: string;
  dateTime?: string; // ISO 8601 format
}

export interface Post {
  id: string;
  authorId: string;
  authorUsername?: string;
  title?: string;
  content: string;
  language?: string;
  tags?: PostTag[];
  createdAt: string;
  updatedAt: string;
  edited?: boolean;
  deleted?: boolean;
  deletionReason?: string;
  /** Flagging (moderator view): when flagged, who flagged, status, reason */
  flaggedAt?: string;
  flaggedBy?: string;
  flagStatus?: string;
  flagReason?: string;
  /** Optional reward for finding the connection (e.g. dinner, coffee) */
  reward?: string;
  /** Optional sighting type (e.g. missed-connection, give thanks) */
  sightingType?: SightingType;
  /** Optional pairing constellation (M_M, W_W, M_W, W_M, OTHER) */
  pairingType?: PairingType;
}

export interface PostFilters {
  category?: string;
  searchIntent?: string;
  /** Filter by sighting type */
  sightingType?: SightingType;
  lat?: number;
  lon?: number;
  radiusKm?: number;
  fromDate?: string; // ISO 8601 format
  toDate?: string; // ISO 8601 format
  page?: number;
  size?: number;
  sort?: string;
}

export interface PostTagRequest {
  tagType: TagType;
  tagValue?: string;
  latitude?: number;
  longitude?: number;
  locationName?: string;
  dateTime?: string; // ISO 8601 format
}

export interface CreatePostRequest {
  title?: string;
  content: string;
  language?: string;
  reward?: string;
  sightingType?: SightingType;
  pairingType?: PairingType;
  tags?: PostTagRequest[];
}

export interface UpdatePostRequest {
  title?: string;
  content?: string;
  language?: string;
  reward?: string;
  sightingType?: SightingType;
  pairingType?: PairingType;
  tags?: PostTagRequest[];
}


export interface PaginatedResponse<T> {
  content: T[];
  totalElements: number;
  totalPages: number;
  size: number;
  number: number;
  first: boolean;
  last: boolean;
}

